/**
 * The frame for the signed-in customer's screens: the shared app shell with the account's
 * navigation and a menu to sign out, then the page header. With no session it shows the brand and
 * a sign-in gate instead, because a navigation to screens that need a session would only send a
 * stranger to more sign-in prompts.
 */
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { AppShell } from "@lattice/ui/AppShell";
import { BuildingIcon, CardIcon, ClipboardIcon, DocumentIcon, FlaskIcon, LockIcon, PackageIcon } from "@lattice/ui/icons";
import { Banner } from "@lattice/ui/Banner";
import type { ContentWidth } from "@lattice/ui/Content";
import { Content } from "@lattice/ui/Content";
import { Link } from "@lattice/ui/Link";
import { Mark } from "@lattice/ui/Mark";
import { PageHeader } from "@lattice/ui/PageHeader";
import { Heading, Text } from "@lattice/ui/Text";
import { signedInEmail, trySignOut } from "../api.ts";
import { EmailCodeSignIn } from "../EmailCodeSignIn.tsx";
import { navigate, usePath } from "../router.ts";

/* Kept across pages, which each mount their own shell, so the email is not fetched and redrawn at every navigation. */
let knownEmail: string | null = null;
const LINKS = [{
  href: "/account",
  icon: <DocumentIcon />,
  label: "Formula"
}, {
  href: "/account/orders",
  icon: <PackageIcon />,
  label: "Orders"
}, {
  href: "/account/billing",
  icon: <CardIcon />,
  label: "Subscription"
}, {
  href: "/account/labs",
  icon: <FlaskIcon />,
  label: "Labs"
}, {
  href: "/account/intake",
  icon: <ClipboardIcon />,
  label: "Health answers"
}, {
  href: "/account/clinics",
  icon: <BuildingIcon />,
  label: "Clinics"
}, {
  href: "/account/privacy",
  icon: <LockIcon />,
  label: "Your data"
}] as const;
function SignedOut({
  heading
}: {
  readonly heading: string;
}) {
  return <div className="x1ov3xa9 x1l0w1cl xzb3lse x1cikjda x1rf3zrh xbz0q3o x179o8vp x1u8hu9d">
      <div className="x1jb01v5 xvueqy4">
        <a href="/" className="x78zum5 x6s0dn4 x1173whf x1gg8mnh x1i7hzv8 x10dbofl x1hl2dhg xqu410z x8wwnw1 xeq5yr9" aria-label="Lattice home">
          <Mark size={22} />
          <span className="xzb3lse">Lattice</span>
        </a>
        <main className="x78zum5 xdt5ytf x15agyyk xowlazn xwlyjvd">
          <div className="x78zum5 xdt5ytf x626il7">
            <Heading level={1}>{heading}</Heading>
            <Text tone="secondary">Enter your email and we'll send a six-digit code.</Text>
          </div>
          <EmailCodeSignIn next={window.location.pathname + window.location.search} />
        </main>
      </div>
    </div>;
}

/** The sentence every failed account load ends on, with its last words a real link to support. */
export const TRY_AGAIN_THEN_SUPPORT = <>
    Try again in a minute. If it keeps happening,{" "}
    <Link href="/support" navigate={navigate}>
      write to support
    </Link>
    .
  </>;
export function AccountShell({
  title,
  signedOut,
  gateTitle = "Sign in to manage your data",
  showSignOut = true,
  width = "medium",
  children
}: {
  readonly title: string;
  readonly signedOut?: boolean;
  readonly gateTitle?: string;
  /** False on a page a signed-out visitor can see, where a Sign out item would have no session to end. */
  readonly showSignOut?: boolean;
  readonly width?: ContentWidth;
  readonly children?: ReactNode;
}) {
  const path = usePath();
  const [email, setEmail] = useState<string | null>(knownEmail);
  const [signOutFailed, setSignOutFailed] = useState(false);
  useEffect(() => {
    if (signedOut) return;
    let current = true;
    void signedInEmail().then(found => {
      if (!current) return;
      knownEmail = found;
      setEmail(found);
    });
    return () => {
      current = false;
    };
  }, [signedOut]);
  if (signedOut) return <SignedOut heading={gateTitle} />;
  return <AppShell brand="Lattice" navigate={navigate} navLabel="Account" nav={[{
    items: LINKS.map(link => ({
      ...link,
      current: path === link.href || link.href === "/account" && path === "/account/formula"
    }))
  }]} account={{
    name: email ?? "Your account",
    items: showSignOut ? [{
      label: "Sign out",
      onSelect: () => {
        setSignOutFailed(false);
        void trySignOut().then(ended => {
          if (ended) {
            knownEmail = null;
            navigate("/");
          } else setSignOutFailed(true);
        });
      }
    }] : [{
      label: "Home",
      onSelect: () => navigate("/")
    }]
  }}>
      <Content width={width}>
        {signOutFailed ? <Banner tone="danger" title="Not signed out" onDismiss={() => setSignOutFailed(false)}>
            We could not sign you out. Check your connection and try again from the account menu.
          </Banner> : null}
        <PageHeader title={title} siteName="Lattice" navigate={navigate} />
        {children}
      </Content>
    </AppShell>;
}